<template>
  <div class="tw-flex tw-items-center tw-justify-center">
    <svg :viewBox="viewBox" :style="svgStyles">
      <circle
        v-bind="circleAttributes"
        :style="circleStyles"
        shape-rendering="geometricPrecision"
        class="tw-fill-none tw-stroke-black/10"
      />

      <path
        :d="path"
        :style="pathStyles"
        class="tw-fill-none"
        :class="props.pathClasses"
        style="stroke-linecap: round"
      />
    </svg>
  </div>
</template>

<script lang="ts" setup>
const props = withDefaults(
  defineProps<{
    percentage?: number
    radius?: number
    thickness?: number
    pathClasses?: string
  }>(),
  {
    percentage: 0,
    radius: 40,
    thickness: 14,
    pathClasses: 'tw-stroke-riser-blue'
  }
)

const viewBoxSize = computed(() => {
  // thickness is not multiplied by 2 as it expands in both directions from the given radius
  return props.radius * 2 + props.thickness
})

const viewBox = computed(() => {
  return `0 0 ${viewBoxSize.value} ${viewBoxSize.value}`
})

const center = computed(() => {
  return { x: viewBoxSize.value / 2, y: viewBoxSize.value / 2 }
})

const circleAttributes = computed(() => {
  return {
    cx: center.value.x,
    cy: center.value.y,
    r: props.radius
  }
})

const svgStyles = computed(() => {
  return {
    height: viewBoxSize.value + 'px',
    width: viewBoxSize.value + 'px'
  }
})

const pathStyles = computed(() => {
  const styles = {
    'stroke-width': props.thickness
  }

  return styles
})

const circleStyles = computed(() => {
  return {
    'stroke-width': props.thickness
  }
})

const path = computed(() => {
  /*
    there are always two ways of drawing the circle, a long one and a short one
    below 50 percent the short one is the one that follows our wanted circle
    above 50 percent, however, there is a shorter path and we don't want this one to be used
  */
  const useLong = props.percentage >= 50

  //clamp percentage below 100 to ensure a circle is being drawn
  const targetPosition = percentageToPosition(props.percentage < 100 ? props.percentage : 99.9999)

  //move to the top middle of the svg
  let pathString = `M ${center.value.x} ${center.value.y - props.radius} `

  //draw the actual arch
  pathString += `A ${props.radius} ${props.radius} 1 ${useLong ? 1 : 0} 1 ${targetPosition.x} ${
    targetPosition.y
  } `

  //if the percentage is equal to or above 100 we want to close the circle
  if (props.percentage >= 100) pathString += 'Z'

  return pathString
})

const percentageToPosition = (percentage: number) => {
  const angle = (360 / 100) * percentage
  const radian = angle * 0.0174532925

  return {
    x: center.value.x + Math.sin(radian) * props.radius,
    y: center.value.y - Math.cos(radian) * props.radius
  }
}
</script>
